import {
  ActionCellRenderer,
  ActionTypes,
} from '../../../../utils/custom-renderer/action-cell-renderer';
import {
  formatMMDDYYYY,
  formatCascadeTimestampToDateTime,
  getFormattedNumToFixedDecimals,
  getFormattedNumber,
  getNumericPattern,
  getStatusColour,
  getStatusTranslate,
} from '../../../../utils/formatting';
import { getAbbreviationForCreditProfile } from '../../../../utils/credit-profiles';
import type { IndividualProjectVppasGrid } from './individual-projects-vppas-grid';
import { copyIcon, deleteIcon, pencilIcon, starUnHighlightedIcon } from '../../../../styles';
import {
  CONTRACT_TYPE,
  ICONS,
  ICON_COLORS,
  PROFILE_RIGHTS,
  VPPA_RFQ_STATUS,
} from '../../../../utils';
import { CASCADE_ENUM } from '../../../../services/enum.service';
import {
  HyperlinkStaticCellRenderer,
  IGridHyperlink,
} from '../../../../utils/custom-renderer/hyperlink-cell-renderer';
import { Profile } from '../../../../utils/profiles';
import {
  CUSTOM_HEADER_TOOLTIPS,
  getGroupColumnSameObject,
  headerWithTooltipColParams,
} from '../../../../utils/grid';
import { CreditProfileLegendTooltipComponent } from '../../../../utils/custom-renderer/credit-profile-legend-tooltip-component';

function getStatusStyle(params: any) {
  const response = {};

  if (params.data?.VPPA_STATUS) {
    response['color'] = getStatusColour(params.data?.VPPA_STATUS);
  }

  return response;
}

function getMaxWeight(context: IndividualProjectVppasGrid): number {
  if (context.userService.getFilteredPermissions()?.includes(PROFILE_RIGHTS.BUYER_VISIBILITY)) {
    return 210;
  }

  return 140;
}

function getActions(context: IndividualProjectVppasGrid, params): Array<any> {
  let actions = [];

  if (
    context.userService.getFilteredPermissions()?.includes(PROFILE_RIGHTS.BUYER_VISIBILITY) &&
    !context.userService.getFilteredPermissions()?.includes(Profile.TRANSACTION_ADVISOR) &&
    !context.userService.getFilteredPermissions()?.includes(Profile.READ_ONLY_BUYER)
  ) {
    const documentBtn = {
      name: 'Create Proposal',
      callback: (detail) => {
        context.$emit('createProposal', detail);
      },
      type: ActionTypes.CREATE,
      actionName: 'confirm-trade',
      disabled: params.data.VPPA_STATUS === VPPA_RFQ_STATUS.AWAITING_KEY_METRICS,
    };
    const watchlistBtn = {
      name: starUnHighlightedIcon(),
      callback: (detail) => {
        context.$emit('favoriteWatchlistVppa', detail);
      },
      type: ActionTypes.FAVORITE,
      actionName: 'favorite',
      tooltip: 'Add to Watchlist',
    };

    const downloadBtn = {
      name: `<zero-icon variant="solid" size="lg" name="download"></zero-icon>`,
      callback: (detail) => {
        context.$emit('downloadVppa', detail);
      },
      type: ActionTypes.DOWNLOAD,
      actionName: 'download-vppa',
      tooltip: 'Download Key Metrics',
    };

    if (params.data.CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED) {
      actions = actions.concat(...[watchlistBtn, downloadBtn]);
    } else {
      actions = actions.concat(...[documentBtn, watchlistBtn, downloadBtn]);
    }
  }

  if (context.userService.getFilteredPermissions()?.includes(PROFILE_RIGHTS.SELLER_VISIBILITY)) {
    const copyBtn = {
      name: copyIcon(),
      callback: (detail) => {
        context.$emit('copyVppa', detail);
      },
      type: ActionTypes.EDIT,
      actionName: 'copy-vppa',
      tooltip: 'Copy',
    };
    const editBtn = {
      name: pencilIcon(),
      callback: (detail) => {
        context.$emit('editVppa', detail);
      },
      type: ActionTypes.EDIT,
      actionName: 'edit-vppa',
      tooltip: 'Edit',
    };

    const deleteBtn = {
      name: deleteIcon(),
      callback: (detail) => {
        context.$emit('deleteVppa', detail);
      },
      type: ActionTypes.DELETE,
      actionName: 'delete-vppa',
      tooltip: 'Delete',
    };

    const downloadBtn = {
      name: `<zero-icon variant="solid" size="lg" name="download"></zero-icon>`,
      callback: (detail) => {
        context.$emit('downloadVppa', detail);
      },
      type: ActionTypes.DOWNLOAD,
      actionName: 'download-vppa',
      tooltip: 'Download Key Metrics',
    };

    actions = actions.concat(...[downloadBtn, copyBtn, editBtn, deleteBtn]);
  }

  return actions;
}

export enum COLUMN_FIELDS {
  TICKER = 'TICKER',
  UNIQUE_ID = 'UNIQUE_ID',
  SETTLEMENT_POINT = 'SETTLEMENT_POINT',
  TENOR_START_DATE = 'TENOR_START_DATE',
  TENOR_MONTHS = 'TENOR_MONTHS',
  FIXED_PRICE = 'FIXED_PRICE',
  OFFERED_CAPACITY = 'OFFERED_CAPACITY',
  SELLER_POST_COD_COLLATERAL = 'SELLER_POST_COD_COLLATERAL',
  VPPA_STATUS = 'VPPA_STATUS',
  ACTIONS = 'ACTIONS',
  KEY_METRIC_TIMESTAMP = 'KEY_METRIC_TIMESTAMP',
}

export function VPPA_COLUMNS(context: IndividualProjectVppasGrid) {
  return [
    {
      field: COLUMN_FIELDS.TICKER,
      headerName: 'Ticker',
      flex: 1,
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      valueGetter: (params) => {
        const iconList: Array<{ icon: ICONS; width; height; fill? }> = [];

        if (params.data.CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED) {
          iconList.push({
            icon: ICONS.AUTO_EXECUTED_ICON,
            width: '16px',
            height: '16px',
            fill: ICON_COLORS.AUTO_EXECUTED_STATUS,
          });
        }

        return JSON.stringify({
          NAME: params.data.TICKER,
          ICON_LIST: iconList,
        });
      },
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        if (!params.value && !params.data) return;

        const obj = JSON.parse(params.value);
        const hyperlinkParams: IGridHyperlink = {
          value: obj?.NAME,
          id: '',
          vppaId: null,
          iconList: obj?.ICON_LIST,
        };

        return hyperlinkParams;
      },
    },
    {
      field: COLUMN_FIELDS.UNIQUE_ID,
      headerName: 'Unique ID',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.SETTLEMENT_POINT,
      headerName: 'Settlement Point',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.OFFERED_CAPACITY,
      headerName: 'Offered Capacity (MW)',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      filterValueGetter: (params) => {
        return getFormattedNumber(params.data.OFFERED_CAPACITY);
      },
      valueFormatter: (params) => getFormattedNumber(params.data.OFFERED_CAPACITY),
    },
    {
      field: COLUMN_FIELDS.FIXED_PRICE,
      headerName: 'Fixed Price',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterValueGetter: (params) => {
        return getFormattedNumToFixedDecimals(params.data.FIXED_PRICE, '', 2, '$');
      },
      valueFormatter: (params) => {
        return getFormattedNumToFixedDecimals(params.data.FIXED_PRICE, '', 2, '$');
      },
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.TENOR_START_DATE,
      headerName: 'Start Date',
      filterValueGetter: (params) => {
        return formatMMDDYYYY(params.data.TENOR_START_DATE);
      },
      valueFormatter: (params) => {
        return formatMMDDYYYY(params.data.TENOR_START_DATE);
      },
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.TENOR_MONTHS,
      headerName: 'Tenor (mo)',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.SELLER_POST_COD_COLLATERAL,
      headerName: 'Credit Profile ($/MW)',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterValueGetter: (params) => {
        return getNumericPattern(
          params.data.SELLER_POST_COD_COLLATERAL,
          getAbbreviationForCreditProfile(params.data.VPPA_CREDIT_PROFILE),
          '$',
        );
      },
      valueFormatter: (params) => {
        return getNumericPattern(
          params.data.SELLER_POST_COD_COLLATERAL,
          getAbbreviationForCreditProfile(params.data.VPPA_CREDIT_PROFILE),
          '$',
        );
      },
      flex: 1,
      ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.CREDIT_PROFILE),
    },
    {
      field: COLUMN_FIELDS.KEY_METRIC_TIMESTAMP,
      headerName: 'Key Metrics Last Updated',
      filterValueGetter: (params) => {
        return formatCascadeTimestampToDateTime(params.data.KEY_METRIC_TIMESTAMP);
      },
      valueFormatter: (params) => {
        return formatCascadeTimestampToDateTime(params.data.KEY_METRIC_TIMESTAMP);
      },
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.VPPA_STATUS,
      headerName: 'Status',
      flex: 1,
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      cellStyle: (params) => getStatusStyle(params),
      valueFormatter: (params) => getStatusTranslate(params.data.VPPA_STATUS),
    },
    {
      field: COLUMN_FIELDS.ACTIONS,
      headerName: 'Actions',
      suppressMenu: true,
      maxWidth: getMaxWeight(context),
      suppressRowClickSelection: true,
      pinned: 'right',
      cellStyle: {
        'padding-right': '7px',
        'box-shadow': '-5px 0px 15px 0px rgba(0, 0, 0, 0.2)',
      },
      cellRenderer: ActionCellRenderer,
      cellRendererParams: (params) => {
        const actions = getActions(context, params);
        const enabled = true;
        return {
          actions,
          enabled,
        };
      },
    },
  ];
}
